import { Fragment, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@breathelife/mui';
import { FutureDateConditionValue, LastIncidentDateConditionValue } from '@zinnia/form-engine-sdk';

import {
  getConditionValidationError,
  ValidationValues,
} from '../../../Helpers/inputValidation/form/salesDecisionRules';
import { NumberInput } from '../../NumberInput/NumberInput';
import { DateUnitSelect } from '../../SelectInputs/DateUnitSelect';
import { CollectionOperators } from '../Helpers/CollectionOperators';
import { ConditionTargetNodeAutocomplete } from '../Helpers/ConditionTargetNodeAutocomplete';
import { NumberComparisonConditionOperatorSelect } from '../Helpers/NumberComparisonConditionOperatorSelect';
import { ValidationErrorText } from '../ValidationErrorText';
import { ConditionCriteriaProps } from '../types';

export function DateConditionCriteria({
  condition,
  nodeIdList,
  collectionContext,
  nodeIdInCollectionMap,
  onConditionPropertiesChange,
  language,
  conditionIndex,
}: ConditionCriteriaProps<FutureDateConditionValue | LastIncidentDateConditionValue>): ReactElement {
  const { t } = useTranslation();
  const validationErrors = getConditionValidationError(condition, nodeIdList, nodeIdInCollectionMap);

  const [comparisonValue, setComparisonValue] = useState<number | undefined>(condition.value);

  const onBlur = useCallback(() => {
    onConditionPropertiesChange({ value: comparisonValue });
  }, [onConditionPropertiesChange, comparisonValue]);

  const collectionNodeIds = nodeIdInCollectionMap[condition.targetNodeId] ?? [];

  return (
    <Fragment>
      <Grid item xs={12}>
        <ConditionTargetNodeAutocomplete
          value={condition.targetNodeId}
          nodeIdList={nodeIdList}
          onSelect={(targetNodeId) => {
            if (targetNodeId !== null) {
              onConditionPropertiesChange({ targetNodeId });
            }
          }}
          validationError={validationErrors?.[ValidationValues.targetNodeId]}
          selectedLanguage={language}
        />
      </Grid>
      <Grid item xs={12}>
        <NumberComparisonConditionOperatorSelect
          value={condition.operator}
          onChange={(operator) => {
            onConditionPropertiesChange({ operator });
          }}
        />
        {validationErrors?.[ValidationValues.operator] && (
          <ValidationErrorText>{validationErrors[ValidationValues.operator].message}</ValidationErrorText>
        )}
      </Grid>
      <Grid item xs={12}>
        <NumberInput
          inputVariant='outlined'
          value={comparisonValue}
          onNumberChange={setComparisonValue}
          label={t('admin.conditions.labels.controlValue')}
          onBlur={onBlur}
        />
        {validationErrors?.[ValidationValues.value] && (
          <ValidationErrorText>{validationErrors[ValidationValues.value].message}</ValidationErrorText>
        )}
      </Grid>
      <Grid item xs={12}>
        <DateUnitSelect
          value={condition.unit}
          onChange={(unit) => {
            onConditionPropertiesChange({ unit });
          }}
        />
        {validationErrors?.[ValidationValues.unit] && (
          <ValidationErrorText>{validationErrors[ValidationValues.unit].message}</ValidationErrorText>
        )}
      </Grid>
      <CollectionOperators
        collectionContext={collectionContext}
        collectionNodeIds={collectionNodeIds}
        collectionOperators={condition.collectionOperators}
        onConditionPropertiesChange={onConditionPropertiesChange}
        conditionIndex={conditionIndex}
      />
    </Fragment>
  );
}
